import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import KakaoLoginButton from "../components/KakaoLoginButton";
import { isValidKoreanMobile, formatKoreanMobile } from "../services/phone";
import api from "../services/api";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');

.register-page {
  min-height: 100vh;
  background: #f9f7f4;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'DM Sans', sans-serif;
  padding: 32px 16px;
  position: relative;
  overflow: hidden;
}

.register-blob {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
}
.register-blob-1 {
  width: 460px; height: 460px;
  background: radial-gradient(circle, rgba(26,92,56,0.07) 0%, transparent 70%);
  top: -120px; right: -120px;
}
.register-blob-2 {
  width: 300px; height: 300px;
  background: radial-gradient(circle, rgba(26,92,56,0.05) 0%, transparent 70%);
  bottom: -60px; left: -60px;
}

.register-card {
  background: #fff;
  border-radius: 24px;
  border: 1px solid #e8e4df;
  box-shadow: 0 8px 40px rgba(0,0,0,0.08);
  padding: 44px 40px;
  width: 100%;
  max-width: 460px;
  position: relative;
  z-index: 1;
}

.register-logo {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}
.register-logo img {
  height: 42px;
  object-fit: contain;
}

.register-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
  margin-bottom: 6px;
}
.register-subtitle {
  font-size: 0.88rem;
  color: #8a8a8a;
  text-align: center;
  margin-bottom: 28px;
}

.rf-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.rf-field { margin-bottom: 14px; }
.rf-field.full { grid-column: 1 / -1; }

.rf-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #1a5c38;
  margin-bottom: 6px;
}
.rf-input-wrap { position: relative; }
.rf-input {
  width: 100%;
  border: 1.5px solid #e8e4df;
  border-radius: 11px;
  padding: 11px 14px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.92rem;
  color: #1a1a1a;
  background: #fff;
  outline: none;
  transition: border-color 0.18s;
  box-sizing: border-box;
}
.rf-input:focus { border-color: #1a5c38; }
.rf-input::placeholder { color: #bbb; }
.rf-input.has-icon { padding-right: 44px; }

.rf-eye {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #aaa;
  font-size: 0.95rem;
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  transition: color 0.15s;
}
.rf-eye:hover { color: #1a5c38; }

.rf-error {
  background: #fef2f2;
  border: 1px solid #fcd5d5;
  border-radius: 9px;
  padding: 10px 14px;
  font-size: 0.84rem;
  color: #d94f3d;
  margin-bottom: 14px;
}

.rf-submit {
  width: 100%;
  background: #1a5c38;
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 14px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.96rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 6px;
  transition: background 0.18s, transform 0.14s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.rf-submit:hover { background: #134429; transform: translateY(-1px); }

.rf-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 22px 0;
  color: #ccc;
  font-size: 0.8rem;
}
.rf-divider::before, .rf-divider::after {
  content: ''; flex: 1; height: 1px; background: #e8e4df;
}

.rf-login-row {
  text-align: center;
  font-size: 0.88rem;
  color: #8a8a8a;
}
.rf-login-row a {
  color: #1a5c38;
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.15s;
}
.rf-login-row a:hover { opacity: 0.75; }

@media (max-width: 480px) {
  .register-card { padding: 32px 20px; border-radius: 18px; }
  .register-title { font-size: 1.5rem; }
  .rf-row { grid-template-columns: 1fr; gap: 0; }
}
`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref") || "";
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", email: "",
    password: "", phoneNumber: "", profileImageUrl: null,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error,        setError]        = useState("");
  const [loading,      setLoading]      = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Format the mobile as they type, so what they see matches what we store.
    setFormData({
      ...formData,
      [name]: name === "phoneNumber" ? formatKoreanMobile(value) : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // A delivery needs a real mobile — and it is what stops one person farming
    // referral bonuses across a pile of throwaway accounts.
    if (!isValidKoreanMobile(formData.phoneNumber)) {
      const msg = t("auth.invalidPhone");
      setError(msg);
      toast.error(msg);
      return;
    }

    setLoading(true);
    try {
      await api.post("/users/register", { ...formData, referral_code: referralCode });
      toast.success(t("auth.registerSuccess"));
      setFormData({ firstName: "", lastName: "", email: "", password: "", phoneNumber: "", profileImageUrl: null });
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      const msg = err.response?.data?.message || t("auth.registerFailed");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{S}</style>
      <div className="register-page">
        <div className="register-blob register-blob-1" />
        <div className="register-blob register-blob-2" />

        <div className="register-card">
          {/* Logo */}
          <div className="register-logo">
            <img src="/images/Logo1.png" alt="FMart" />
          </div>

          <h1 className="register-title">{t("auth.createAccount")}</h1>
          <p className="register-subtitle">{t("auth.registerSubtitle")}</p>

          {referralCode && (
            <div style={{ background: "#f0f9f4", border: "1px solid #c5dece", color: "#1a5c38", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: "0.9rem" }}>
              🎁 {t("auth.referralBanner")}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Name row */}
            <div className="rf-row">
              <div className="rf-field">
                <span className="rf-label">{t("auth.firstName")}</span>
                <input className="rf-input" type="text" name="firstName"
                  value={formData.firstName} onChange={handleChange}
                  placeholder={t("auth.firstName")} required />
              </div>
              <div className="rf-field">
                <span className="rf-label">{t("auth.lastName")}</span>
                <input className="rf-input" type="text" name="lastName"
                  value={formData.lastName} onChange={handleChange}
                  placeholder={t("auth.lastName")} required />
              </div>
            </div>

            {/* Email */}
            <div className="rf-field">
              <span className="rf-label">{t("auth.email")}</span>
              <input className="rf-input" type="email" name="email"
                value={formData.email} onChange={handleChange}
                placeholder="your@email.com" required />
            </div>

            {/* Password */}
            <div className="rf-field">
              <span className="rf-label">{t("auth.password")}</span>
              <div className="rf-input-wrap">
                <input className="rf-input has-icon"
                  type={showPassword ? "text" : "password"}
                  name="password" value={formData.password} onChange={handleChange}
                  placeholder={t("auth.strongPasswordPlaceholder")} required />
                <button type="button" className="rf-eye" onClick={() => setShowPassword(v => !v)}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Phone — the courier calls this, so it must be a real mobile */}
            <div className="rf-field">
              <span className="rf-label">{t("auth.phoneNumber")}</span>
              <input className="rf-input" type="tel" name="phoneNumber"
                value={formData.phoneNumber} onChange={handleChange}
                placeholder="010-1234-5678" inputMode="numeric" maxLength={13} required />
            </div>

            {/* Error */}
            {error && <div className="rf-error">{error}</div>}

            {/* Submit */}
            <button type="submit" className="rf-submit" disabled={loading}>
              {loading ? t("auth.creatingAccount") : t("auth.createAccountBtn")}
            </button>
          </form>

          {/* Carries the ?ref= code through Kakao, so a Kakao signup still
              credits whoever invited them. */}
          <KakaoLoginButton next="/" referralCode={referralCode || null} />

          <div className="rf-divider">{t("auth.or")}</div>

          <div className="rf-login-row">
            {t("auth.haveAccount")}{" "}
            <Link to="/login">{t("auth.signIn")}</Link>
          </div>
        </div>

        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </>
  );
};

export default RegisterPage;